import {
  Button,
  MenuItem,
  Stack,
  TextField,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  type ChangeEvent,
  type FormEvent,
  memo,
  useCallback,
  useState,
} from "react";
import { SmsConsentStatus } from "../../generated/graphql";
import { type LineTypeFilter, STATUS_LABELS } from "./sms-consent-display";

export type StatusFilter = SmsConsentStatus | "ALL";

const styles = {
  filters: { mb: 2, alignItems: "center" } satisfies SxProps<Theme>,
  searchField: { minWidth: 260 } satisfies SxProps<Theme>,
  selectField: { minWidth: 200 } satisfies SxProps<Theme>,
};

type Props = {
  readonly searchedNumber: string | null;
  readonly onSearchedNumberChange: (phoneNumber: string | null) => void;
  readonly status: StatusFilter;
  readonly onStatusChange: (status: StatusFilter) => void;
  readonly lineType: LineTypeFilter;
  readonly onLineTypeChange: (lineType: LineTypeFilter) => void;
};

/**
 * The search box and the two dropdowns above the consent table. The search
 * only applies on submit, because it matches a full number exactly and a
 * half-typed one would be rejected.
 */
const SmsConsentFilters = memo(
  ({
    searchedNumber,
    onSearchedNumberChange,
    status,
    onStatusChange,
    lineType,
    onLineTypeChange,
  }: Props) => {
    const [searchDraft, setSearchDraft] = useState("");

    const onSearchDraftChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setSearchDraft(event.target.value);
      },
      [],
    );

    const onSearch = useCallback(
      (event: FormEvent) => {
        event.preventDefault();
        const trimmed = searchDraft.trim();
        onSearchedNumberChange(trimmed === "" ? null : trimmed);
      },
      [onSearchedNumberChange, searchDraft],
    );

    const onClearSearch = useCallback(() => {
      setSearchDraft("");
      onSearchedNumberChange(null);
    }, [onSearchedNumberChange]);

    const onStatusSelect = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onStatusChange(event.target.value as StatusFilter);
      },
      [onStatusChange],
    );

    const onLineTypeSelect = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onLineTypeChange(event.target.value as LineTypeFilter);
      },
      [onLineTypeChange],
    );

    return (
      <Stack
        component="form"
        direction="row"
        spacing={2}
        sx={styles.filters}
        onSubmit={onSearch}
      >
        <TextField
          label="Full phone number"
          placeholder="(504) 555-0123"
          size="small"
          sx={styles.searchField}
          value={searchDraft}
          onChange={onSearchDraftChange}
        />
        <Button type="submit" variant="contained">
          Search
        </Button>
        {searchedNumber !== null && (
          <Button variant="text" onClick={onClearSearch}>
            Show all
          </Button>
        )}
        <TextField
          select
          label="Status"
          size="small"
          sx={styles.selectField}
          value={status}
          onChange={onStatusSelect}
        >
          <MenuItem value="ALL">All statuses</MenuItem>
          {Object.values(SmsConsentStatus).map((value) => (
            <MenuItem key={value} value={value}>
              {STATUS_LABELS[value]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Line type"
          size="small"
          sx={styles.selectField}
          value={lineType}
          onChange={onLineTypeSelect}
        >
          <MenuItem value="ALL">All line types</MenuItem>
          <MenuItem value="LANDLINE">Landline</MenuItem>
          <MenuItem value="MOBILE">Mobile</MenuItem>
        </TextField>
      </Stack>
    );
  },
);

SmsConsentFilters.displayName = "SmsConsentFilters";

export default SmsConsentFilters;
